import { useState, useEffect, useRef } from "react";
import { 
  Play, Radio, Heart, Music, Video, Users, Mail, Phone, MapPin, 
  ChevronDown, ChevronUp, Menu, X, Tv, BookOpen, HandHeart, Film,
  Shield, FileText, Clock, Globe, Star, ArrowRight, Check, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/eh8zi2sq_logo.svg";
const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbziqVd2-Kj6G_ffGAFcuye5FIzRems5riA2c5GUT335xHXXX7yFM31MxyQblid3RB9I/exec";
const SCRIPT_SECRET = "gospeltube_secret_2026";

// Navigation Component
const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMobileOpen(false);
      }
    };

    if (mobileOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [mobileOpen]);

  return (
    <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="GospelTube" className="h-10 w-auto" />
          </a>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-features">Features</a>
            <a href="#about" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-about">About</a>
            <a href="#faq" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
            <a href="#contact" className="text-gray-300 hover:text-white transition-colors" data-testid="nav-contact">Contact</a>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <Button 
              className="bg-brand hover:bg-brand-dark text-white rounded-full px-6 py-2 font-medium transition-all hover:scale-105"
              data-testid="nav-waitlist-btn"
              onClick={() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Join Waitlist
            </Button>
          </div>

          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>Features</a>
              <a href="#about" className="text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>About</a>
              <a href="#faq" className="text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>FAQ</a>
              <a href="#contact" className="text-gray-300 hover:text-white py-2" onClick={() => setMobileOpen(false)}>Contact</a>
              <Button 
                className="bg-brand hover:bg-brand-dark text-white rounded-full w-full mt-2"
                onClick={() => {
                  setMobileOpen(false);
                  document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Join Waitlist
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    setError("");
    
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "waitlist", email, secret: SCRIPT_SECRET })
      });
      setSubmitted(true);
      setEmail("");
    } catch (err) {
      console.error("Waitlist error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1507692049790-de58290a4334?w=1920&q=80" 
          alt="Church worship background"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-[#0A0A0A]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,165,0,0.1)_0%,transparent_70%)]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <div className="animate-fade-in-up opacity-0">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand/10 border border-brand/20 text-brand text-sm font-medium mb-8">
            <img src="https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/3uqndtku_icon.png" alt="GospelTube" className="w-5 h-5" />
            Now Onboarding Churches & Creators
          </span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight mb-6 animate-fade-in-up opacity-0 delay-100">
          Streaming <span className="gradient-text">Faith</span>
          <br />to the Nations
        </h1>

        <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12 animate-fade-in-up opacity-0 delay-200">
          A faith & purpose streaming platform for churches, music ministers, creators, 
          wellness advocates, charities, and uplifting movies. Helping communities share 
          inspiring content and grow support.
        </p>

        <div className="animate-fade-in-up opacity-0 delay-300" id="waitlist">
          {submitted ? (
            <div className="flex items-center justify-center gap-3 text-green-400 text-lg">
              <Check size={24} />
              <span>Thank you! We'll be in touch soon.</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-brand text-white placeholder:text-gray-400 rounded-full px-6 py-6 text-lg flex-1 focus:border-brand focus:ring-brand"
                data-testid="waitlist-email-input"
                required
                disabled={loading}
              />
              <Button 
                type="submit"
                className="bg-brand hover:bg-brand-dark text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 animate-pulse-glow"
                data-testid="waitlist-submit-btn"
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : "Join Waitlist"}
              </Button>
            </form>
          )}
          {error && <p className="text-red-400 mt-4">{error}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-8 mt-16 animate-fade-in-up opacity-0 delay-400">
          <div className="flex items-center gap-2 text-gray-400">
            <Globe size={20} />
            <span>Global Streaming</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <Shield size={20} />
            <span>Secure Platform</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <Clock size={20} />
            <span>24/7 Access</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section
const features = [
  {
    icon: Radio,
    title: "Live Streaming",
    description: "Broadcast live church services, events, and special programs to your global congregation in real-time.",
    image: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&q=80",
    gradient: "from-brand/20 to-transparent"
  },
  {
    icon: Music,
    title: "Music Ministrations",
    description: "Share powerful worship sessions, gospel music performances, and praise concerts with believers worldwide.",
    image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80",
    gradient: "from-amber-500/20 to-transparent"
  },
  {
    icon: BookOpen,
    title: "Devotionals & Teachings",
    description: "Access transformative Bible studies, devotionals, and faith-centered teachings from renowned ministers.",
    image: "https://images.unsplash.com/photo-1504052434569-70ad5836ab65?w=600&q=80",
    gradient: "from-blue-500/20 to-transparent"
  },
  {
    icon: Heart,
    title: "Wellness & Mental Health",
    description: "Discover content focused on spiritual wellness, mental health encouragement, and holistic well-being.",
    image: "https://images.unsplash.com/photo-1758273240631-59d44c8f5b66?w=600&q=80",
    gradient: "from-green-500/20 to-transparent"
  },
  {
    icon: HandHeart,
    title: "Charity & Outreach",
    description: "Stay updated with charity stories, outreach initiatives, and opportunities to make a difference.",
    image: "https://images.unsplash.com/photo-1593113598332-cd288d649433?w=600&q=80",
    gradient: "from-purple-500/20 to-transparent"
  },
  {
    icon: Film,
    title: "Uplifting Movies",
    description: "Watch faith-based films that promote strong values, hope, and inspire positive transformation.",
    image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600&q=80",
    gradient: "from-brand/20 to-transparent"
  }
];

const FeaturesSection = () => {
  return (
    <section id="features" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-brand text-sm font-medium uppercase tracking-widest mb-4 block">
            What We Offer
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Everything Your Faith Community Needs
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            A comprehensive platform designed to serve churches, creators, and believers worldwide.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group relative bg-zinc-900/50 border border-white/5 hover:border-brand/50 rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-brand/10"
              data-testid={`feature-card-${index}`}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient}`}></div>
              </div>
              
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent"></div>
              </div>

              <div className="relative p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-xl bg-brand/20 text-brand">
                    <feature.icon size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white">{feature.title}</h3>
                </div>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const builtFor = [
    { title: "Churches", desc: "live and on-demand services, events, teachings, and devotionals" },
    { title: "Music ministers & creators", desc: "channels, uploads, and audience growth tools" },
    { title: "Wellness advocates", desc: "faith-friendly encouragement and mental health support content" },
    { title: "Charities", desc: "storytelling, outreach updates, and giving opportunities" },
    { title: "Uplifting movies", desc: "values-based films and inspirational storytelling" },
  ];

  return (
    <section id="about" className="py-24 md:py-32 bg-zinc-900/30" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="text-brand text-sm font-medium uppercase tracking-widest mb-4 block">
              Who We Are
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About GospelTube
            </h2>
            <p className="text-lg text-gray-400 mb-6 leading-relaxed">
              <strong className="text-white">GospelTube Inc</strong> is a faith & purpose streaming platform built to amplify content that <strong className="text-white">encourages, heals, and inspires</strong>. We bring together <strong className="text-white">churches, music ministers, gospel content creators, mental health advocates, bloggers, charity organizations, and producers of uplifting, values-based movies</strong>, all in one trusted place.
            </p>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">
              <strong className="text-white">Our mission is simple:</strong> make it easier to <strong className="text-white">discover uplifting content</strong>, <strong className="text-white">support ministries and charities</strong>, and <strong className="text-white">build a positive community</strong> across <strong className="text-white">iOS, Android, web, and smart TVs</strong>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-3 text-gray-300">
                <div className="p-2 rounded-lg bg-white/10">
                  <MapPin size={20} className="text-brand" />
                </div>
                <span>New York, USA</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <div className="p-2 rounded-lg bg-white/10">
                  <Users size={20} className="text-brand" />
                </div>
                <span>GospelTube Inc</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-semibold text-white mb-6">
              Built for Ministries, Creators, and Communities
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              GospelTube is designed for organizations and creators who want to share meaningful content and grow impact:
            </p>
            <div className="space-y-4">
              {builtFor.map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-zinc-900/50 border border-white/5 rounded-xl p-4">
                  <div className="p-1.5 rounded-full bg-brand/20 mt-0.5">
                    <Check size={14} className="text-brand" />
                  </div>
                  <div>
                    <span className="text-white font-medium">{item.title}:</span>
                    <span className="text-gray-400 ml-1">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-gray-400 mt-6 leading-relaxed">
              <strong className="text-white">Our goal:</strong> provide a reliable platform where audiences can stream with confidence, and where creators and organizations can build trust, expand reach, and strengthen communities.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Product Preview Section
const productScreenshots = [
  {
    image: "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/do5rnwo7_generated-image-1%20%281%29.png",
    title: "Join Live Church Services",
    description: "Stream live services from churches worldwide"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/w73xehxd_IMG_1238.PNG",
    title: "Follow Ministries You Love",
    description: "Discover and follow your favorite ministries"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/dy57kgpn_IMG_1239%202.PNG",
    title: "Explore Church Profiles",
    description: "Learn about churches and their content"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/wup65alv_IMG_1241.PNG",
    title: "Grow, Connect, Be Inspired",
    description: "Access devotionals and faith-centered content"
  }
];

const ProductPreviewSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="py-24 md:py-32 bg-[#0A0A0A] overflow-hidden" data-testid="product-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-brand text-sm font-medium uppercase tracking-widest mb-4 block">
            Product Preview
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            See GospelTube in Action
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Experience our faith-based streaming platform designed for churches, creators, and believers.
          </p>
        </div>

        {/* Screenshot Carousel */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Phone Mockup with Active Screenshot */}
          <div className="relative flex justify-center">
            <div className="absolute -inset-4 bg-[radial-gradient(circle_at_center,rgba(255,165,0,0.15)_0%,transparent_70%)]"></div>
            <div className="relative w-64 md:w-72">
              <div className="relative bg-zinc-900 rounded-[2.5rem] p-2 shadow-2xl border border-white/10">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-10"></div>
                <div className="rounded-[2rem] overflow-hidden bg-black">
                  <img 
                    src={productScreenshots[activeIndex].image}
                    alt={productScreenshots[activeIndex].title}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Screenshot Selector */}
          <div className="space-y-4">
            {productScreenshots.map((item, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-full text-left p-4 rounded-xl border transition-all duration-300 ${
                  activeIndex === index 
                    ? 'bg-brand/10 border-brand' 
                    : 'bg-zinc-900/50 border-white/5 hover:border-white/20'
                }`}
                data-testid={`screenshot-btn-${index}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 ${
                    activeIndex === index ? 'border-brand' : 'border-transparent'
                  }`}>
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${activeIndex === index ? 'text-brand' : 'text-white'}`}>
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm">{item.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button 
            className="bg-brand hover:bg-brand-dark text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 group"
            onClick={() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="product-preview-waitlist-btn"
          >
            Get Early Access
            <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// App Showcase Section
const AppShowcaseSection = () => {
  return (
    <section className="py-24 md:py-32 bg-zinc-900/30 overflow-hidden" data-testid="app-showcase-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-brand text-sm font-medium uppercase tracking-widest mb-4 block">
              Coming Soon
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Experience GospelTube Anywhere
            </h2>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">
              Our mobile app is currently in testing on the App Store. Soon you'll be able to 
              stream uplifting content, watch live services, and connect with your faith community 
              from any device, anywhere in the world.
            </p>

            <div className="space-y-4 mb-8">
              {[
                "Stream live church services in HD quality",
                "Access on-demand devotionals and teachings",
                "Support your favorite ministries with easy donations",
                "Personalized recommendations based on your interests"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="p-1 rounded-full bg-brand/20">
                    <Check size={16} className="text-brand" />
                  </div>
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>

            <Button 
              className="bg-brand hover:bg-brand-dark text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 group"
              onClick={() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="app-showcase-waitlist-btn"
            >
              Get Early Access
              <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          <div className="relative flex justify-center">
            <div className="absolute -inset-4 bg-[radial-gradient(circle_at_center,rgba(255,165,0,0.15)_0%,transparent_70%)]"></div>
            
            {/* Phone Mockup */}
            <div className="relative w-64 md:w-72">
              <div className="relative bg-zinc-900 rounded-[2.5rem] p-2 shadow-2xl border border-white/10">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-10"></div>
                <div className="rounded-[2rem] overflow-hidden bg-black">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_soulcontent-1/artifacts/do5rnwo7_generated-image-1%20%281%29.png"
                    alt="GospelTube App Preview"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const faqs = [
  {
    question: "Is there a cost associated with using GospelTube?",
    answer: "GospelTube will offer both free and premium content. Churches and creators can share content freely, while premium features like HD streaming and exclusive content may require a subscription. We're committed to keeping faith accessible to everyone."
  },
  {
    question: "Can I watch past church services if I missed the live stream?",
    answer: "Absolutely! All live streams are automatically saved and made available for on-demand viewing. You can catch up on any service, sermon, or event at your convenience."
  },
  {
    question: "How do I make donations through the app?",
    answer: "GospelTube provides a secure, integrated donation system. You can support your favorite churches, ministries, and creators directly through the platform with multiple payment options."
  },
  {
    question: "What devices does GospelTube support?",
    answer: "GospelTube will be available on iOS, Android, web browsers, and smart TVs. Watch on your phone, tablet, laptop, or living room TV, wherever you prefer."
  },
  {
    question: "How do churches and creators get started on GospelTube?",
    answer: "Join our waitlist and we'll reach out with onboarding details. We're currently onboarding churches and creators for our beta launch. The process is simple and our team will guide you every step of the way."
  }
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" className="py-24 md:py-32 bg-zinc-900/30" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-brand text-sm font-medium uppercase tracking-widest mb-4 block">
            FAQ
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-400">
            Everything you need to know about GospelTube.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-zinc-900/50 border border-white/5 rounded-xl overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <button
                className="w-full px-6 py-5 flex items-center justify-between text-left"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                data-testid={`faq-btn-${index}`}
              >
                <span className="text-lg font-medium text-white pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp size={24} className="text-brand flex-shrink-0" />
                ) : (
                  <ChevronDown size={24} className="text-gray-400 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "contact", ...formData, secret: SCRIPT_SECRET })
      });
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="text-brand text-sm font-medium uppercase tracking-widest mb-4 block">
              Get In Touch
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Contact Us
            </h2>
            <p className="text-lg text-gray-400 mb-12 leading-relaxed">
              Have questions about GospelTube? Want to partner with us or report an issue? 
              We'd love to hear from you. Our team is here to help.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-brand/20 text-brand">
                  <Mail size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">Email</p>
                  <a href="mailto:info@gospeltube.tv" className="text-white text-lg hover:text-brand transition-colors" data-testid="contact-email">
                    info@gospeltube.tv
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-brand/20 text-brand">
                  <MapPin size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">Location</p>
                  <span className="text-white text-lg" data-testid="contact-location">New York, USA</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="p-4 rounded-full bg-green-500/20 text-green-400 mb-4">
                  <Check size={32} />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-2">Message Sent!</h3>
                <p className="text-gray-400">We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="contact-name-input"
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="contact-email-input"
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help?"
                    rows={4}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
                    data-testid="contact-message-input"
                    required
                    disabled={loading}
                  />
                </div>
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <Button 
                  type="submit"
                  className="w-full bg-brand hover:bg-brand-dark text-white rounded-xl py-6 text-lg font-medium transition-all hover:scale-[1.02]"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-24 md:py-32 bg-gradient-to-b from-[#0A0A0A] to-zinc-900" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="relative">
          <div className="absolute -inset-4 bg-[radial-gradient(circle_at_center,rgba(255,165,0,0.15)_0%,transparent_70%)]"></div>
          <div className="relative">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Stream Faith-Based Content?
            </h2>
            <p className="text-xl text-gray-400 mb-10">
              Join thousands of churches, creators, and believers on GospelTube. 
              Be among the first to experience the future of faith streaming.
            </p>
            <Button 
              className="bg-brand hover:bg-brand-dark text-white rounded-full px-10 py-7 text-xl font-medium transition-all hover:scale-105 animate-pulse-glow"
              onClick={() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="cta-waitlist-btn"
            >
              Join the Waitlist Today
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-black border-t border-white/5 py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="GospelTube" className="h-10 w-auto" />
            </div>
            <p className="text-gray-400 mb-6 max-w-md">
              A faith & purpose streaming platform for churches, music ministers, creators, 
              wellness advocates, charities, and uplifting movies.
            </p>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} GospelTube Inc. All rights reserved.
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              <li><a href="#about" className="text-gray-400 hover:text-white transition-colors">About</a></li>
              <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
              <li><a href="#faq" className="text-gray-400 hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              <li><a href="/terms" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</a></li>
              <li><a href="/privacy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</a></li>
              <li><a href="/content-policy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-content-policy">Content Policy</a></li>
              <li><a href="/dmca" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-dmca">Copyright/DMCA</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <a href="mailto:info@gospeltube.tv" className="hover:text-white transition-colors">info@gospeltube.tv</a>
              <span>|</span>
              <span>New York, USA</span>
            </div>
            <p className="text-sm text-gray-500">
              Content is reviewed and creators must follow community guidelines.
            </p>
          </div>
          <p className="text-xs text-gray-600 mt-6 text-center">
            Disclaimer: GospelTube is an independent platform. Any names, images, or metrics displayed in demo materials are for illustrative purposes only and do not constitute endorsement or partnership unless expressly stated.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      <HeroSection />
      <FeaturesSection />
      <ProductPreviewSection />
      <AboutSection />
      <AppShowcaseSection />
      <FAQSection />
      <ContactSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default LandingPage;
